import axios from 'axios';
import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { Container, Spinner } from 'react-bootstrap';

import { getUser, renewTokens } from 'services/user';

import { useLocalStorage } from '@ui/hooks/useLocalStorage';
import { useTracking } from '@ui/hooks/useTracking';
import { useLanguage } from '@ui/hooks/useLanguage';
import { useNavigate } from 'react-router-dom';
import useImpersonatedAsQueryStrings from '@ui/hooks/useImpersonatedAsQueryString';
import { useLazyLaunchDarkly } from './useLazyLaunchDarkly';
import { usePostHog } from '@posthog/react';

const UseUser = React.createContext(null);

const AppContainer = styled(Container)`
  width: 100%;
  height: 100%;
  overflow: auto;
  text-align: center;
`;

const getTokenExpDate = (token) => {
  if (token && token.split) {
    const encodedPayload = token.split('.')[1];
    const { exp } = JSON.parse(atob(encodedPayload));

    return exp * 1000;
  }

  return null;
};

const getJwtToken = async (tokens) => {
  try {
    const newTokenResponse = await renewTokens(tokens.refreshToken);

    if (newTokenResponse.success) {
      return newTokenResponse.data;
    }

    return null;
  } catch (e) {
    return null;
  }
};

export function UserSelectorContextProvider({ children }) {
  const [jwtTokens, setJwtTokens, clearJwtTokens] = useLocalStorage('jwt-tokens');
  const { impersonatedAs } = useImpersonatedAsQueryStrings();
  const [user, setUser] = useState(null);
  const [registerPopupDisplayed, setRegisterPopupDisplayed] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { identify } = useTracking();
  const { language, setLanguage } = useLanguage();
  const navigate = useNavigate();
  const { setUserContext } = useLazyLaunchDarkly();

  const posthog = usePostHog()

  const userEmail = useMemo(() => {
    if (!user?.email) {
      return '';
    }

    if (user.email.length < 17) {
      return user.email;
    }

    return `${user.email.slice(0, 8)}...${user.email.slice(user.email.length - 6, user.email.length)}`;
  }, [user]);

  const userName = useMemo(() => {
    if (!user?.first_name || !user?.last_name) {
      return '-';
    }

    const name = `${user?.first_name} ${user?.last_name}`;

    if (name.length < 23) {
      return name;
    }

    return `${name.slice(0, 10)}...${name.slice(name.length - 10, name.length)}`;
  }, [user]);

  const hasCompletedBankDetails = useMemo(() => !!user?.withdrawal_details, [user]);
  const isUserProfileCompleted = useMemo(() => !!user?.profile_setup_completed, [user]);
  const isOnboardingPersonalDataCompleted = useMemo(
    () => !!user?.first_name && !!user?.last_name && !!user?.dob,
    [user]
  );
  const isPropertyFinderUser = useMemo(() => !!user?.products?.property_finder, [user]);
  const isCountryServicesUser = useMemo(() => !!user?.products?.country_services, [user]);
  const isPropertyFinderContactAgentStage1Submitted = useMemo(
    () => !!user?.property_finder_contact_agents_stage_1_completed,
    [user]
  );
  const isExistingMortgageUser = useMemo(() => !!user?.products?.passport, [user]);
  const calendlyEventDate = useMemo(() => {
    if (+new Date() < +new Date(user?.calendly_event_date)) {
      return user.calendly_event_date;
    }

    return null;
  }, [user]);
  const scheduledBookings = useMemo(() => user?.scheduled_bookings || [], [user]);
  const assignedCalendlyUrl = useMemo(() => user?.assigned_calendly_url || null, [user]);
  const scheduledMortgageBookings = useMemo(() => user?.scheduled_mortgage_bookings || [], [user]);
  const assignedMortgageCalendlyUrl = useMemo(
    () => user?.assigned_mortgage_calendly_url || null,
    [user]
  );

  const reloadUser = () =>
    getUser().then((data) => {
      setUser(data);
    });

  const reloadUserPropertyFinderSessions = useCallback(
    async () =>
      getUser().then((data) => {
        setUser((user) => {
          user.property_finder_sessions = data.property_finder_sessions;

          return user;
        });
      }),
    [setUser]
  );

  const logout = useCallback(() => {
    posthog.reset?.();
    clearJwtTokens();
    setIsLoggedIn(false);
    setIsLoading(false);
    navigate('/login');
  }, [clearJwtTokens, setIsLoggedIn, setIsLoading, navigate, posthog]);

  useEffect(() => {
    if (jwtTokens) {
      axios.defaults.headers.common.Authorization = jwtTokens.accessToken;
      axios.defaults.headers.common.IdToken = jwtTokens.idToken;
      axios.defaults.headers.common.ImpersonatedAs = impersonatedAs;

      const tokenExpiration = getTokenExpDate(jwtTokens.accessToken);

      if (tokenExpiration <= +new Date()) {
        getJwtToken(jwtTokens).then((newJwtTokens) => {
          if (newJwtTokens) {
            setJwtTokens(newJwtTokens);

            axios.defaults.headers.common.Authorization = newJwtTokens.accessToken;
            axios.defaults.headers.common.IdToken = newJwtTokens.idToken;
            axios.defaults.headers.common.ImpersonatedAs = impersonatedAs;
          } else {
            logout();
          }
        });
      } else {
        getUser()
          .then((data) => {
            setUser(data);
            setIsLoading(false);
            setIsLoggedIn(true);

            if (
              data?.preferred_language?.lang &&
              data?.preferred_language?.lang !== language?.lang
            ) {
              setLanguage(data.preferred_language);
            }
          })
          .catch(() => {
            clearJwtTokens();
            window.location.href = '/';
          });
      }
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line
  }, [jwtTokens]);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        if (error.response && error.response.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          if (jwtTokens) {
            return getJwtToken(jwtTokens).then((newJwtTokens) => {
              if (newJwtTokens) {
                axios.defaults.headers.common.Authorization = newJwtTokens.accessToken;
                axios.defaults.headers.common.IdToken = newJwtTokens.idToken;
                axios.defaults.headers.common.ImpersonatedAs = impersonatedAs;

                originalRequest.headers['Authorization'] = newJwtTokens.accessToken;
                originalRequest.headers['IdToken'] = newJwtTokens.idToken;
                originalRequest.headers['ImpersonatedAs'] = impersonatedAs;

                setJwtTokens(newJwtTokens);

                return axios(originalRequest);
              } else {
                logout();
              }
            });
          } else {
            return Promise.reject(error);
          }
        } else {
          if (error.response) {
            return error.response;
          }
          return Promise.reject(error);
        }
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (user) {
      identify({
        data: {
          user_id: user.user_id,
          register_type: user.register_type,
          created_at: user.created_at,
          email: user.email,
          phone_number: user.phone_number
        }
      });

      posthog.identify(
        user.user_id,
        {
          has_property_finder: !!user?.products?.property_finder,
          has_passport: !!user?.products?.passport,
        },
        {
          register_type: user.register_type,
          created_at: user.created_at,
        }
      )

      posthog.people.set({
        email: user.email,
        phone_number: user.phone_number
      })

      posthog.register_for_session(
        {
          impersonated: !!impersonatedAs,
          impersonated_as: impersonatedAs,
        }
      )

      // Store user context for LaunchDarkly (used when LD is initialized)
      setUserContext(user);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, posthog]);

  if (isLoading) {
    return (
      <AppContainer fluid id="scrollable">
        <Spinner animation="grow" size="lg" />
      </AppContainer>
    );
  }

  return (
    <UseUser.Provider
      value={{
        user,
        userEmail,
        userName,
        reloadUser,
        reloadUserPropertyFinderSessions,
        isLoggedIn,
        isLoading,
        jwtTokens,
        clearJwtTokens,
        hasCompletedBankDetails,
        isUserProfileCompleted,
        isOnboardingPersonalDataCompleted,
        isPropertyFinderUser,
        isCountryServicesUser,
        registerPopupDisplayed,
        setRegisterPopupDisplayed,
        isImpersonated: impersonatedAs && impersonatedAs === user?.email,
        isPropertyFinderContactAgentStage1Submitted,
        isExistingMortgageUser,
        calendlyEventDate,
        scheduledBookings,
        assignedCalendlyUrl,
        scheduledMortgageBookings,
        assignedMortgageCalendlyUrl
      }}>
      {children}
    </UseUser.Provider>
  );
}

export function useUserSelector() {
  const context = useContext(UseUser);

  if (!context) {
    throw new Error('useUserSelector must be used within a UserSelectorContextProvider');
  }

  return context;
}
